/**
 * PatientPicker — reusable list of patients rendered by config-derived label.
 *
 * Used by AssignPatientDialog, OrgDialogs, the standalone Patients page, and
 * the ward census board. A patient has NO name: each row's primary line comes
 * from `formatPatientLabel(attributes, label_format)`; a dimmer secondary
 * line shows whatever attributes the label format didn't already consume
 * (`formatPatientSecondary`) — its `{ward}` placeholder resolves via
 * `locationById` from the patient's `location_id` (ward is PLACEMENT, not an
 * attribute). Handles the four async states (error w/ retry, loading
 * skeleton, empty, data) per the frontend resilience rules.
 *
 * Selection is optional: when `onSelect` is provided each row is a button
 * (assign / census flow); otherwise rows are static (read-only list).
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { UserRound, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EmptyState, ErrorWithRetry, LoadingSkeleton } from '@/components/clinical';
import type { PatientResponse } from '@/api/endpoints/patients';
import type { Location } from '@/api/endpoints/org';
import { formatPatientLabel, formatPatientSecondary } from '@/lib/patientLabel';

interface PatientPickerProps {
  patients: PatientResponse[] | undefined;
  labelFormat: string | undefined;
  isLoading: boolean;
  isError: boolean;
  isFetching?: boolean;
  onRetry: () => void;
  /** When set, rows are selectable buttons that call this with the token. */
  onSelect?: (patient: PatientResponse) => void;
  /** Token of the currently selected/assigned patient (shows a checkmark). */
  selectedToken?: string | null;
  /** Token whose row should show a pending spinner (mid-mutation). */
  pendingToken?: string | null;
  /** Resolves a patient's `location_id` to its unit name for the secondary
   *  line's `{ward}` placeholder. Omitted ⇒ `{ward}` renders empty (callers
   *  without location context, e.g. dialogs that never loaded the org tree). */
  locationById?: Map<string, Location>;
  /** Per-row trailing badge (e.g. a discharged/cancelled status pill) —
   *  return null/undefined for a row that needs no badge. */
  renderBadge?: (patient: PatientResponse) => ReactNode;
  className?: string;
}

/** The patient's small circular identity glyph — never a photo (a patient has
 *  no name, let alone a picture); a plain person glyph tinted to match the
 *  row's selection state. Shared between the picker row and the detail
 *  header so a patient's "avatar" reads the same wherever it appears. */
export function PatientAvatar({ selected = false, className }: { selected?: boolean; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
        selected ? 'bg-primary text-white' : 'bg-neutral-100 text-neutral-500',
        className,
      )}
    >
      <UserRound className="h-[18px] w-[18px]" />
    </span>
  );
}

export function PatientPicker({
  patients,
  labelFormat,
  isLoading,
  isError,
  isFetching = false,
  onRetry,
  onSelect,
  selectedToken,
  pendingToken,
  locationById,
  renderBadge,
  className,
}: PatientPickerProps) {
  const { t } = useTranslation();

  // ERROR (no cached data) — explicit, with retry.
  if (isError && !patients) {
    return (
      <ErrorWithRetry
        error={t('patients.list.loadError.title')}
        onRetry={onRetry}
        className={className}
      />
    );
  }

  // LOADING (no cached data) — skeleton, never a blank screen.
  if (isLoading && !patients) {
    return <LoadingSkeleton shape="list-item" count={4} className={className} />;
  }

  // EMPTY — meaningful, not blank.
  if (patients && patients.length === 0) {
    return (
      <EmptyState
        title={t('patients.list.empty.title')}
        description={t('patients.list.empty.description')}
      />
    );
  }

  // DATA (possibly stale while a background refetch runs, or after a failed
  // background refetch that still has cached data to show).
  return (
    <div className={cn('space-y-1.5', className)}>
      {(isFetching || isError) && patients && (
        <p className="text-[13px] text-neutral-500" role="status">
          {t('patients.list.stale')}
        </p>
      )}
      {(patients ?? []).map((patient) => {
        const wardName = patient.location_id ? locationById?.get(patient.location_id)?.name : undefined;
        // formatPatientLabel returns the token-derived codename immediately
        // whenever a token is passed (patient.token always is here) — it never
        // reaches wardName/attributes, so only pass what it actually uses.
        const label = formatPatientLabel(patient.attributes, labelFormat, patient.token);
        const secondary = formatPatientSecondary(patient.attributes, labelFormat, wardName);
        const isSelected = selectedToken === patient.token;
        const isPending = pendingToken === patient.token;
        const badge = renderBadge?.(patient);

        if (onSelect) {
          return (
            <button
              key={patient.token}
              type="button"
              onClick={() => onSelect(patient)}
              disabled={isPending}
              aria-pressed={isSelected}
              className={cn(
                // 18px = the utility-card step of the radius ladder (DESIGN.md
                // {rounded.lg}); 14px was between two rungs.
                'flex w-full min-h-[56px] items-center gap-3 rounded-[18px] border p-2.5 text-left',
                'transition active:scale-[0.98] disabled:opacity-60',
                isSelected
                  ? 'border-primary bg-primary/5 ring-1 ring-inset ring-primary/20'
                  : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50',
              )}
            >
              <PatientAvatar selected={isSelected} />
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="truncate text-[15px] font-semibold text-neutral-900">{label}</span>
                  {badge}
                </span>
                {secondary && (
                  <span className="block truncate text-[13px] text-neutral-500">{secondary}</span>
                )}
              </span>
              {isPending ? (
                <span
                  className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-primary border-t-transparent"
                  aria-hidden="true"
                />
              ) : isSelected ? (
                <Check className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
              ) : null}
            </button>
          );
        }

        return (
          <div
            key={patient.token}
            className="flex min-h-[56px] items-center gap-3 rounded-[18px] border border-neutral-200 p-2.5"
          >
            <PatientAvatar />
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2">
                <span className="truncate text-[15px] font-semibold text-neutral-900">{label}</span>
                {badge}
              </span>
              {secondary && (
                <span className="block truncate text-[13px] text-neutral-500">{secondary}</span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}
